import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FeedbackService } from "./service";
import { ErrorToast } from "../../shared/toasters/toasters";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import Pagination from "../../Common/Pagination";

const messageCellStyle: React.CSSProperties = {
  maxWidth: "400px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "default",
};

const FeedbackList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const entriesPerPageOptions = [
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "30", value: 30 },
    { label: "40", value: 40 },
  ];

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const fetchData = async () => {
    try {
      const response = await FeedbackService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
      });
      setList(response.data ?? []);
      setTotalPages(response.totalPages ?? 0);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage]);

  const getUserName = (user: any) => {
    if (!user) return "-";
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return name || "-";
  };

  const getRoleBadge = (user: any) => {
    if (!user?.roles?.length) return "-";
    const role = user.roles[0];
    const map: Record<string, { label: string; color: string }> = {
      ROLE_COMPANY_ADMIN: { label: "Company Admin", color: "primary" },
      ROLE_COMPANY_USER: { label: "Company User", color: "info" },
      ROLE_USER: { label: "Coach", color: "success" },
    };
    const badge = map[role];
    if (!badge) return role;
    return <span className={`badge bg-${badge.color}`}>{badge.label}</span>;
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("feedback.entriesPerPage", "Entries per page")}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("feedback.entriesPerPage", "Entries per page")}</span>
              </ul>
            </div>

            <div className="table-responsive">
              <table className="table table-hover" id="pc-dt-feedback">
                <thead>
                  <tr>
                    <th>{t("feedback.id", "ID")}</th>
                    <th>{t("feedback.submittedBy", "Submitted by")}</th>
                    <th>{t("feedback.email", "Email")}</th>
                    <th>{t("feedback.role", "Role")}</th>
                    <th>{t("feedback.message", "Message")}</th>
                    <th>{t("feedback.status", "Status")}</th>
                    <th>{t("feedback.createdAt", "Date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/feedbacks/${item.id}`)}
                    >
                      <td>{item.id}</td>
                      <td>{getUserName(item.submittedBy)}</td>
                      <td>{item.submittedBy?.email ?? "-"}</td>
                      <td>{getRoleBadge(item.submittedBy)}</td>
                      <td style={{ maxWidth: "400px", padding: 0 }}>
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 200, hide: 300 }}
                          overlay={
                            <Tooltip
                              id={`feedback-tooltip-${item.id}`}
                              style={{ maxWidth: 360, whiteSpace: "pre-wrap" }}
                            >
                              {item.message}
                            </Tooltip>
                          }
                        >
                          <div style={messageCellStyle} className="px-3 py-2">
                            {item.message}
                          </div>
                        </OverlayTrigger>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Badge bg={item.isResolved ? "success" : "secondary"}>
                          {item.isResolved
                            ? t("feedback.resolved", "Resolved")
                            : t("feedback.open", "Open")}
                        </Badge>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t("feedback.previous", "Previous")}
              nextLabel={t("feedback.next", "Next")}
            />
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default FeedbackList;
